import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../../utils/errors';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { ITherapy } from '../../../../../pages/common';
import type { IntakeQuestionnaireState } from '../../types';
import type {
  DeleteIntakeTherapyPayload,
  IntakeTherapyThunks,
  SetIntakeHasNoTherapiesPayload,
} from './types';

export function addIntakeTherapyCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeTherapyThunks,
) {
  addCreateIntakeTherapyCases(builder, thunks);
  addUpdateIntakeTherapyCases(builder, thunks);
  addDeleteIntakeTherapyCases(builder, thunks);
  addDeleteAllIntakeTherapiesCases(builder, thunks);
  addSetIntakeHasNoTherapiesCases(builder, thunks);
}

function addCreateIntakeTherapyCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeTherapyThunks,
) {
  const { createIntakeTherapy } = thunks;

  builder
    .addCase(createIntakeTherapy.pending, (state) => {
      state.createTherapyLoading = true;
      state.error = null;
    })
    .addCase(createIntakeTherapy.fulfilled, (state, action: PayloadAction<ITherapy>) => {
      state.createTherapyLoading = false;
      if (!state.intakeQuestionnaireDetails) return;
      if (!state.intakeQuestionnaireDetails.therapies) {
        state.intakeQuestionnaireDetails.therapies = [];
      }
      state.intakeQuestionnaireDetails.therapies.push(action.payload);
    })
    .addCase(createIntakeTherapy.rejected, (state, action) => {
      state.createTherapyLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addUpdateIntakeTherapyCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeTherapyThunks,
) {
  const { updateIntakeTherapy } = thunks;

  builder
    .addCase(updateIntakeTherapy.pending, (state) => {
      state.updateTherapyLoading = true;
      state.error = null;
    })
    .addCase(updateIntakeTherapy.fulfilled, (state, action: PayloadAction<ITherapy>) => {
      state.updateTherapyLoading = false;
      const list = state.intakeQuestionnaireDetails?.therapies;
      if (!list) return;
      const index = list.findIndex((row) => row.id === action.payload.id);
      if (index !== -1) list[index] = action.payload;
    })
    .addCase(updateIntakeTherapy.rejected, (state, action) => {
      state.updateTherapyLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addDeleteIntakeTherapyCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeTherapyThunks,
) {
  const { deleteIntakeTherapy } = thunks;

  builder
    .addCase(deleteIntakeTherapy.pending, (state) => {
      state.deleteTherapyLoading = true;
      state.error = null;
    })
    .addCase(
      deleteIntakeTherapy.fulfilled,
      (state, action: PayloadAction<void, string, { arg: DeleteIntakeTherapyPayload }>) => {
        state.deleteTherapyLoading = false;
        if (!state.intakeQuestionnaireDetails) return;
        state.intakeQuestionnaireDetails.therapies = (
          state.intakeQuestionnaireDetails.therapies ?? []
        ).filter((row) => row.id !== action.meta.arg.therapyId);
      },
    )
    .addCase(deleteIntakeTherapy.rejected, (state, action) => {
      state.deleteTherapyLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addDeleteAllIntakeTherapiesCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeTherapyThunks,
) {
  const { deleteAllIntakeTherapies } = thunks;

  builder
    .addCase(deleteAllIntakeTherapies.pending, (state) => {
      state.deleteAllTherapiesLoading = true;
      state.error = null;
    })
    .addCase(deleteAllIntakeTherapies.fulfilled, (state) => {
      state.deleteAllTherapiesLoading = false;
      if (!state.intakeQuestionnaireDetails) return;
      state.intakeQuestionnaireDetails.therapies = [];
      state.intakeQuestionnaireDetails.has_no_therapies = true;
    })
    .addCase(deleteAllIntakeTherapies.rejected, (state, action) => {
      state.deleteAllTherapiesLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addSetIntakeHasNoTherapiesCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeTherapyThunks,
) {
  const { setIntakeHasNoTherapies } = thunks;

  builder
    .addCase(setIntakeHasNoTherapies.pending, (state) => {
      state.setHasNoTherapiesLoading = true;
      state.error = null;
    })
    .addCase(
      setIntakeHasNoTherapies.fulfilled,
      (state, action: PayloadAction<void, string, { arg: SetIntakeHasNoTherapiesPayload }>) => {
        state.setHasNoTherapiesLoading = false;
        if (!state.intakeQuestionnaireDetails) return;
        state.intakeQuestionnaireDetails.has_no_therapies = action.meta.arg.hasNoTherapies;
      },
    )
    .addCase(setIntakeHasNoTherapies.rejected, (state, action) => {
      state.setHasNoTherapiesLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}
